import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useParams } from '@tanstack/react-router';
import { useMemo } from 'react';

import { useZero } from '@/lib/zero';

export type ReviewComment = {
  /** Unique identifier for the comment */
  gid: string;
  /** Author information */
  author: {
    name: string;
    /** Avatar URL (ideally 32x32 or similar) */
    avatarUrl: string;
  };
  /** Which side the comment range starts on ("left" = old file, "right" = new file) */
  startSide: 'LEFT' | 'RIGHT';
  /** Which side the comment range ends on (usually the same as startSide) */
  endSide: 'LEFT' | 'RIGHT';
  /** Inclusive line number where the comment range starts */
  startLine: number;
  /** Inclusive line number where the comment range ends */
  endLine: number;
  /** Comment body (for now unused in the diff view) */
  text: string;
  /** Path to the file that the comment is associated with */
  path: string;
  /** Whether the comment is outdated (applies to old commit) */
  isOutdated: boolean;
  /** Whether the comment is resolved (for hiding avatars when resolved comments are collapsed) */
  isResolved: boolean;
};

/**
 * Temporary placeholder hook that returns hard-coded review comments so that the
 * diff viewer can render comment highlights & avatars. Replace with a real
 * implementation once backend wiring is ready.
 */
export function useFakeReviewComments(): ReviewComment[] {
  return [
    {
      gid: 'c1',
      author: {
        name: 'Alice',
        avatarUrl: 'https://i.pravatar.cc/32?img=1',
      },
      startSide: 'RIGHT',
      endSide: 'RIGHT',
      startLine: 10,
      endLine: 12,
      text: 'Please double-check this logic.',
      path: 'src/components/mesa-diff/diff.tsx',
      isOutdated: false,
      isResolved: false,
    },
    {
      gid: 'c2',
      author: {
        name: 'Bob',
        avatarUrl: 'https://i.pravatar.cc/32?img=2',
      },
      startSide: 'LEFT',
      endSide: 'LEFT',
      startLine: 20,
      endLine: 20,
      text: 'Nit: typo here.',
      path: 'src/components/mesa-diff/diff.tsx',
      isOutdated: true,
      isResolved: false,
    },
    {
      gid: 'c3',
      author: {
        name: 'Charlie',
        avatarUrl: 'https://i.pravatar.cc/32?img=3',
      },
      startSide: 'RIGHT',
      endSide: 'RIGHT',
      startLine: 32,
      endLine: 34,
      text: 'Can we simplify this?',
      path: 'src/components/mesa-diff/diff.tsx',
      isOutdated: false,
      isResolved: false,
    },
  ];
}

export function useReviewComments(): ReviewComment[] {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });

  const z = useZero();

  const [reviewComments] = useZeroQuery(
    z.query.ReviewCommentsTable.where(({ and, cmp }) =>
      and(
        cmp('owner', owner), 
        cmp('repo', repo), 
        cmp('pullNumber', number),
        cmp('isBad', false)
      )
    )
  );

  // Filter to only parent comments and normalize types
  const reviewCommentsFormatted = useMemo(() => {
    return reviewComments
      .filter((comment) => comment.inReplyToId === null)
      .map((comment) => ({
        gid: comment.gid,
        author: {
          name: comment.authorLogin,
          avatarUrl: comment.author.avatarUrl,
        },
        startSide: comment.side, // TODO: handle different start and end sides
        endSide: comment.side,
        endLine: comment.line ?? 0, // TODO: handle file-level comments
        startLine: comment.startLine ?? comment.line ?? 0,
        text: comment.body,
        path: comment.path,
        isOutdated: comment.isOutdated ?? false,
        isResolved: comment.resolvedById !== null,
      }));
  }, [reviewComments]);

  return reviewCommentsFormatted;
}
